import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BanknoteIcon, CalendarPlusIcon, CheckIcon, ClockIcon, CreditCardIcon, FileTextIcon, LoaderCircleIcon, LockIcon, MapPinIcon, XIcon } from 'lucide-react';
import { cashSteps, readiness, savedCard } from '../../data/loisirs';
interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onPaid: () => void;
}
type Stage = 'choix' | 'carte' | 'traitement' | 'paye' | 'comptant';
const CASH_CODE = 'BRO-4821-7Y';
const secondaryButton = 'inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const primaryButton = 'inline-flex items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';
export function PaymentDialog({
  open,
  onClose,
  onPaid
}: PaymentDialogProps) {
  const [stage, setStage] = useState<Stage>('choix');
  const [useSaved, setUseSaved] = useState(true);
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);
  useEffect(() => {
    if (!open) {
      setStage('choix');
      setUseSaved(true);
    }
  }, [open]);
  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);
  const pay = () => {
    setStage('traitement');
    timerRef.current = window.setTimeout(() => {
      setStage('paye');
      onPaid();
    }, 1400);
  };
  const summary = <dl className="rounded-2xl border border-slate-200 bg-ville-sand p-4 text-sm" data-id="element-2163" data-mp="33805v16tm4w0">
      <div className="flex items-start justify-between gap-4" data-id="element-2164" data-mp="335xkwvkiz1y5">
        <div data-id="element-2165" data-mp="2fipda6zmjgsy">
          <dt className="sr-only" data-id="element-2166" data-mp="2fiflqeyxl1l5">Activité</dt>
          <dd className="font-extrabold text-ville-navy" data-id="element-2167" data-mp="2bh44awq7nmo1">{readiness.activity}</dd>
          <dd className="mt-0.5 text-slate-600" data-id="element-2168" data-mp="2p7z8vwtofhlt">
            {readiness.participant} · {readiness.session}
          </dd>
        </div>
        <div className="text-right" data-id="element-2169" data-mp="2kg7ns1tai432">
          <dt className="sr-only" data-id="element-2170" data-mp="2kf60myz01r4z">Montant</dt>
          <dd className="text-lg font-extrabold text-ville-navy" data-id="element-2171" data-mp="1wqwkackk1vl8">{readiness.amount}</dd>
          <dd className="text-xs text-slate-500" data-id="element-2172" data-mp="1rsv4q1mc8eei">{readiness.reference}</dd>
        </div>
      </div>
    </dl>;
  return <AnimatePresence data-id="element-2173" data-mp="79hthxrsafvb">
      {open && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-2174" data-mp="78g6cuxhu2x8">
          <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/45" data-id="element-2175" data-mp="397btnumq10m" />

          <motion.div role="dialog" aria-modal="true" aria-label="Paiement de l’inscription" initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-2176" data-mp="uyu1wvfaejwk">
            <div className="flex items-start justify-between gap-4" data-id="element-2177" data-mp="3m04dyl5ui5qm">
              <div data-id="element-2178" data-mp="3h82twd2fraq1">
                <p className="text-xs font-bold uppercase tracking-wider text-ville-indigo" data-id="element-2179" data-mp="3h7jkxlurjwo5">
                  {stage === 'comptant' ? 'Paiement en argent comptant' : 'Paiement de l’inscription'}
                </p>
                <h2 className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2180" data-mp="389hgewbtmlw4">
                  {stage === 'paye' ? 'Paiement reçu' : `${readiness.amount} à payer`}
                </h2>
              </div>
              <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2181" data-mp="4p37wsstgc3k">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-2182" data-mp="2flbu4554cou6" />
              </button>
            </div>

            <div className="mt-5" data-id="element-2183" data-mp="25d7mah1uetdr">
              {stage === 'choix' && <div data-id="element-2184" data-mp="2aewwxvl5l51b">
                  {summary}
                  <p className="mt-5 text-[15px] font-bold text-ville-navy" data-id="element-2185" data-mp="2fdrp3o50g5y3">Comment souhaitez-vous payer ?</p>
                  <div className="mt-3 space-y-3" data-id="element-2186" data-mp="2k4qa310y1s7o">
                    <button type="button" onClick={() => setStage('carte')} className="flex w-full items-start gap-3 rounded-2xl border border-slate-200 p-4 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2187" data-mp="2k5ja7j5edjgu">
                      <CreditCardIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2188" data-mp="13pyg4jo5gcw9" />
                      <span data-id="element-2189" data-mp="18ors27nsfvub">
                        <span className="block text-sm font-extrabold text-ville-navy" data-id="element-2190" data-mp="18q30iks6hrrr">Carte en ligne</span>
                        <span className="block text-sm text-slate-600" data-id="element-2191" data-mp="10p52ag003oj3">
                          Confirmation immédiate · reçu envoyé par courriel
                        </span>
                      </span>
                    </button>
                    <button type="button" onClick={() => setStage('comptant')} className="flex w-full items-start gap-3 rounded-2xl border border-slate-200 p-4 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2192" data-mp="1ak6r1aqd6ol5">
                      <BanknoteIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2193" data-mp="1odei91aj9i2b" />
                      <span data-id="element-2194" data-mp="22cxqrv79avf5">
                        <span className="block text-sm font-extrabold text-ville-navy" data-id="element-2195" data-mp="22e8rfm5enn21">
                          Argent comptant, au comptoir
                        </span>
                        <span className="block text-sm text-slate-600" data-id="element-2196" data-mp="2h062qy8e34s6">
                          Place gardée jusqu’au {readiness.payBefore}
                        </span>
                      </span>
                    </button>
                  </div>
                </div>}

              {stage === 'carte' && <div data-id="element-2197" data-mp="2cx9kx9qobcpa">
                  {summary}

                  <fieldset className="mt-5" data-id="element-2198" data-mp="1y9ephlla6u8i">
                    <legend className="text-[15px] font-bold text-ville-navy" data-id="element-2199" data-mp="1y8lqj6skd90x">Moyen de paiement</legend>
                    <label className={`mt-3 flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors duration-150 ease-ville ${useSaved ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200'}`} data-id="element-2200" data-mp="231y3ukrslted">
                      <input type="radio" name="moyen-paiement" checked={useSaved} onChange={() => setUseSaved(true)} className="h-4 w-4 accent-ville-navy" data-id="element-2201" data-mp="1fdevk58qtgma" />
                      <CreditCardIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2202" data-mp="1ur90y16e8xxv" />
                      <span className="min-w-0 flex-1" data-id="element-2203" data-mp="1lsdwoumwtxx7">
                        <span className="block text-sm font-bold text-ville-navy" data-id="element-2204" data-mp="1lrvs9aq3gde7">
                          {savedCard.brand} •••• {savedCard.last4}
                        </span>
                        <span className="block text-xs text-slate-500" data-id="element-2205" data-mp="1c1j9no1uauap">Expire {savedCard.expiry}</span>
                      </span>
                    </label>

                    <label className={`mt-3 flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors duration-150 ease-ville ${useSaved ? 'border-slate-200' : 'border-ville-indigo bg-ville-indigo-soft'}`} data-id="element-2206" data-mp="131w9t6ordwjr">
                      <input type="radio" name="moyen-paiement" checked={!useSaved} onChange={() => setUseSaved(false)} className="h-4 w-4 accent-ville-navy" data-id="element-2207" data-mp="34rlb8gnh3yi7" />
                      <span className="text-sm font-bold text-ville-navy" data-id="element-2208" data-mp="3ir4ht50mz1se">Utiliser une autre carte</span>
                    </label>
                  </fieldset>

                  {!useSaved && <div className="mt-4 space-y-3" data-id="element-2209" data-mp="2qz5bppts6im1">
                      <div data-id="element-2210" data-mp="2qxkka92rjfse">
                        <label htmlFor="carte-numero" className="block text-xs font-bold text-slate-500" data-id="element-2211" data-mp="2v1ik6lzwwumi">
                          Numéro de carte
                        </label>
                        <input id="carte-numero" inputMode="numeric" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none" data-id="element-2212" data-mp="2zttkvr2h8kg4" />
                      </div>
                      <div className="grid grid-cols-2 gap-3" data-id="element-2213" data-mp="157n87lvx9uax">
                        <div data-id="element-2214" data-mp="19aa6o5n3mdh3">
                          <label htmlFor="carte-exp" className="block text-xs font-bold text-slate-500" data-id="element-2215" data-mp="19c3c5hlz6rni">
                            Expiration
                          </label>
                          <input id="carte-exp" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none" data-id="element-2216" data-mp="hkf5nbpvozcl" />
                        </div>
                        <div data-id="element-2217" data-mp="vcljus9h0w9e">
                          <label htmlFor="carte-cvc" className="block text-xs font-bold text-slate-500" data-id="element-2218" data-mp="vcvcyoptbjjb">
                            Code de sécurité
                          </label>
                          <input id="carte-cvc" inputMode="numeric" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none" data-id="element-2219" data-mp="2smznqbz4xnj" />
                        </div>
                      </div>
                    </div>}

                  <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-500" data-id="element-2220" data-mp="38i8r4zgrz26i">
                    <LockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-2221" data-mp="33ra5rlgxjdwe" />
                    Paiement traité par la passerelle sécurisée de la Ville de Brossard.
                  </p>

                  <div className="mt-6 flex items-center justify-between gap-4" data-id="element-2222" data-mp="2tt45176v0dt4">
                    <button type="button" onClick={() => setStage('choix')} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2223" data-mp="2tqzsy1yt48hr">
                      Retour
                    </button>
                    <button type="button" onClick={pay} className={primaryButton} data-id="element-2224" data-mp="216cjd6x9ol1o">
                      Payer {readiness.amount}
                    </button>
                  </div>
                </div>}

              {stage === 'traitement' && <div className="py-10 text-center" data-id="element-2225" data-mp="2tvjowpv52awf">
                  <LoaderCircleIcon className="mx-auto h-8 w-8 animate-spin text-ville-indigo" aria-hidden="true" data-id="element-2226" data-mp="2yumm5o4vnctu" />
                  <p className="mt-4 text-sm font-semibold text-ville-navy" role="status" data-id="element-2227" data-mp="2k3dy3jgmhzit">
                    Traitement du paiement…
                  </p>
                </div>}

              {stage === 'paye' && <div data-id="element-2228" data-mp="3hkvxbnin6exo">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-2229" data-mp="3hle2j9na83hq">
                    <CheckIcon className="h-6 w-6" data-id="element-2230" data-mp="115r7h47vcjmp" />
                  </span>
                  <h3 className="mt-3 text-lg font-extrabold text-ville-navy" data-id="element-2231" data-mp="1eygxdjqiq8p3">
                    Inscription payée et confirmée
                  </h3>
                  <p className="mt-1 text-sm text-slate-600" data-id="element-2232" data-mp="1aygg7rc1bjec">
                    {readiness.amount} réglé sur {savedCard.brand} •••• {savedCard.last4}. La place d’
                    {readiness.participant} est confirmée pour {readiness.session}.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3" data-id="element-2233" data-mp="w88u91mtji8w">
                    <Link to="/mon-profil" className={secondaryButton} data-id="element-2234" data-mp="r8mlp5hxqq6f">
                      <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-2235" data-mp="rapjibpbxnlp" />
                      Voir mon reçu
                    </Link>
                    <button type="button" className={secondaryButton} data-id="element-2236" data-mp="22ob6prbmpf4l">
                      <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-2237" data-mp="2xxpvytb4xrkf" />
                      Ajouter au calendrier
                    </button>
                  </div>
                  <button type="button" onClick={onClose} className={`mt-4 w-full ${primaryButton}`} data-id="element-2238" data-mp="32yvp2s8pjguh">
                    Terminer
                  </button>
                </div>}

              {stage === 'comptant' && <div data-id="element-2239" data-mp="3gx3t1k45vif5">
                  {summary}

                  <div className="mt-5 rounded-2xl border-2 border-ville-navy bg-white p-4 text-center" data-id="element-2240" data-mp="3gwajkbdtietb">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-2241" data-mp="3ltkfm9vppoq4">
                      Code de paiement
                    </p>
                    <p className="mt-1 text-2xl font-extrabold tracking-[0.18em] text-ville-navy" data-id="element-2242" data-mp="3vlq9xq9fsi8h">
                      {CASH_CODE}
                    </p>
                    <p className="mt-1 flex items-center justify-center gap-1.5 text-xs text-slate-500" data-id="element-2243" data-mp="4dcc8r8rqq3g">
                      <ClockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-2244" data-mp="1sytbej76hlot" />
                      Place gardée jusqu’au {readiness.payBefore}
                    </p>
                  </div>

                  <ol className="mt-5 space-y-4" data-id="element-2245" data-mp="1jy3ibn9ay9es">
                    {cashSteps.map((step, index) => <li key={step.title} className="flex gap-3" data-id="element-2246" data-mp="1eza0xjqw9y9c" data-mpi={index}>
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ville-navy text-xs font-bold text-white" aria-hidden="true" data-id="element-2247" data-mp="1f14pd81dk8i6">
                          {index + 1}
                        </span>
                        <div className="min-w-0" data-id="element-2248" data-mp="22uwdjp215kbr">
                          <p className="text-sm font-bold text-ville-navy" data-id="element-2249" data-mp="1xwus4xh4t8x4">{step.title}</p>
                          <p className="mt-0.5 text-sm text-slate-600" data-id="element-2250" data-mp="2vdjq2g2ynr18">{step.detail}</p>
                        </div>
                      </li>)}
                  </ol>

                  <p className="mt-4 flex items-start gap-1.5 text-xs text-slate-500" data-id="element-2251" data-mp="oohqvqbu6xlf">
                    <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-2252" data-mp="sqd2pore8tdg" />
                    Sans paiement avant l’échéance, la place est remise aux personnes en liste d’attente.
                  </p>

                  <div className="mt-6 flex items-center justify-between gap-4" data-id="element-2253" data-mp="jxhv4oegelki">
                    <button type="button" onClick={() => setStage('choix')} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2254" data-mp="51a9djnedc34">
                      Retour
                    </button>
                    <button type="button" onClick={onClose} className={primaryButton} data-id="element-2255" data-mp="1qdtx70kecnld">
                      J’ai noté le code
                    </button>
                  </div>
                </div>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}